















import { Component, Prop, Vue, Ref } from 'vue-property-decorator'
import ButtonBase from '@/components/common/atoms/ButtonBase.vue'
import jsQR, { QRCode } from 'jsqr'

@Component({
  components: {
    ButtonBase,
  },
})
export default class QrCodeReader extends Vue {
  /** カメラ映像表示フラグ */
  private isDisplayCameraView = false
  /** 映像デバイス読み込み中フラグ */
  private isVideoLoading = false
  /** カメラ映像表示用canvasアニメーションID */
  private animationId?: number

  /** デバイス映像ストリーム用 */
  private videoStreamElem: HTMLVideoElement = document.createElement('video')
  /** カメラ対面モード（初期値）設定 */
  private cameraFacingMode: 'user' | 'environment' = 'environment'

  @Prop({ default: true })
  isStartButton!: boolean

  /** カメラ映像描画先キャンバス */
  @Ref() cameraMonitorRef!: HTMLCanvasElement

  private async mounted() {
    if (!this.isStartButton) this.clickQrLoginButton()
  }

  /**
   * 「二次元コードでログインする」ボタンクリック処理
   */
  private clickQrLoginButton(): void {
    this.isDisplayCameraView = true
    this.startMediaDevice()
  }

  /**
   * 「カメラモード切り替え」ボタンクリック処理
   */
  private async switchCameraFacingMode(): Promise<void> {
    this.cameraFacingMode = this.cameraFacingMode === 'user' ? 'environment' : 'user'
    await this.stopMediaDevice()
    await this.startMediaDevice()
  }

  /**
   * 映像デバイス情報取得・更新、カメラ映像解析処理開始
   */
  public async startMediaDevice(): Promise<void> {
    const video: HTMLVideoElement = this.videoStreamElem
    const canvasAnimationRender: () => void = this.canvasAnimationRender

    this.isVideoLoading = true
    await navigator.mediaDevices
      .getUserMedia({ video: { facingMode: this.cameraFacingMode } })
      .then(function (stream: MediaStream): void {
        video.srcObject = stream
        video.setAttribute('playsinline', 'true') // required to tell iOS safari we don't want fullscreen
        video.play()
        // カメラ映像表示用canvasアニメーション開始
        requestAnimationFrame(canvasAnimationRender)
      })
  }

  /**
   * 映像ストリーム準備中判定
   */
  private isVideoOnLoad(): boolean {
    return !!this.videoStreamElem && this.videoStreamElem.readyState === this.videoStreamElem.HAVE_ENOUGH_DATA
  }

  /**
   * カメラ映像表示用canvasアニメーション処理
   */
  private canvasAnimationRender(): void {
    this.animationId = requestAnimationFrame(this.canvasAnimationRender)
    // 要素が失われたらアニメーション停止（ページ遷移対策）
    if (!this.cameraMonitorRef) {
      this.stopMediaDevice()
      return
    }

    // 映像ストリームが有効になるまで処理しない
    if (this.isVideoOnLoad()) {
      // 映像ストリームサイズを、カメラ映像表示キャンバスサイズに適用
      if (this.isVideoLoading) {
        this.cameraMonitorRef.width = this.videoStreamElem.videoWidth
        this.cameraMonitorRef.height = this.videoStreamElem.videoHeight
        this.isVideoLoading = false
        return
      }

      // カメラ映像表示用キャンバスに映像ストリーム描画
      const canvas = this.cameraMonitorRef.getContext('2d', {
        willReadFrequently: true,
      }) as CanvasRenderingContext2D
      canvas.drawImage(this.videoStreamElem, 0, 0, this.cameraMonitorRef.width, this.cameraMonitorRef.height)

      // カメラ映像表示用キャンバスから映像情報取得
      const imageData: ImageData = canvas.getImageData(0, 0, this.cameraMonitorRef.width, this.cameraMonitorRef.height)
      // QRコード解析
      const qrCode: QRCode | null = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      })

      // QRコード検出判定
      if (qrCode && qrCode.data !== '') {
        Vue.prototype.$logger.log(qrCode)
        // 検出されたQRコード部分にライン描画
        this.drawQrCodeDetectionLine(qrCode.location)
        this.stopMediaDevice()
        // ログイン処理
        this.$emit('read-qr-code', qrCode.data)
      }
    }
  }

  /**
   * 映像デバイス情報取得・更新、カメラ映像解析終了
   */
  private async stopMediaDevice(): Promise<void> {
    // カメラ映像表示用canvasアニメーション停止
    if (this.animationId) cancelAnimationFrame(this.animationId)

    const mediaStream = this.videoStreamElem.srcObject as MediaStream
    if (!mediaStream) return

    // デバイス映像ストリーム終了
    const tracks = mediaStream.getTracks()
    tracks.forEach((track: MediaStreamTrack) => {
      track.stop()
      mediaStream.removeTrack(track)
    })
    this.videoStreamElem.srcObject = null
  }

  /**
   * QRコード検出ライン描画
   */
  private drawQrCodeDetectionLine(qrLocat: QRCode['location']) {
    this.drawLine(qrLocat.topLeftCorner, qrLocat.topRightCorner, '#FF3B58')
    this.drawLine(qrLocat.topRightCorner, qrLocat.bottomRightCorner, '#FF3B58')
    this.drawLine(qrLocat.bottomRightCorner, qrLocat.bottomLeftCorner, '#FF3B58')
    this.drawLine(qrLocat.bottomLeftCorner, qrLocat.topLeftCorner, '#FF3B58')
  }

  /**
   * カメラ映像表示用キャンバスにライン描画
   */
  private drawLine(begin: { x: number; y: number }, end: { x: number; y: number }, color: string) {
    const canvas = this.cameraMonitorRef.getContext('2d') as CanvasRenderingContext2D

    canvas.beginPath()
    canvas.moveTo(begin.x, begin.y)
    canvas.lineTo(end.x, end.y)
    canvas.lineWidth = 4
    canvas.strokeStyle = color
    canvas.stroke()
  }
}
